import firebase from 'firebase/app';
import 'firebase/storage';

/**
 * Gets the details of the tileLogo document with uid as tileLogoUid
 *
 * @export
 * @param {String} tileLogoUid - UID of the tileLogo document
 * @return {Promise} - Promise which succeeds once the request is successful. The promise contains the document data object.
 */
export async function getTileLogoDetails(tileLogoUid) {
    if (tileLogoUid) {
        var db = firebase.firestore();
        var doc = await db.collection('tile_logo').doc(tileLogoUid).get();
        if (doc.exists) {
            return {
                ...doc.data(),
                created_at: doc.get('created_at').toDate(),
                last_updated_at: doc.get('last_updated_at').toDate(),
                id: doc.id,
            };
        } else {
            throw new Error('TileLogo with this ID doesnt exist');
        }
    } else {
        throw new Error('Invalid TileLogo ID');
    }
}

/**
 * Creates a new tileLogo
 * @export
 * @param {Object} name - Name object of the tileLogo
 * @param {File} tileLogoFile - Svg file of the tileLogo
 * @param {String} userId - Id of the user obtained from the user object
 * @return {Promise} - Promise which succeeds once the request is successful. The promise contains the newly created tileLogo's uid.
 */
export async function createTileLogo(name, tileLogoFile, userId) {
    var combinedName = name;
    // Check if tileLogo name is unique
    await isTileLogoNameUnique(combinedName);
    // Create tileLogo record on firebase
    var recordId = await createTileLogoRecordOnDb(name, combinedName, userId);
    // upload image to firebase storage
    var fileUrl = await uploadTileLogoFile(combinedName, tileLogoFile);
    // Add url to uploaded image to created record
    await addFileUrlForTileLogo(recordId, userId, fileUrl);
    return recordId;
}

/**
 * Updates an already created tileLogo
 *
 * @export
 * @param {String} userId - Id of the user obtained from the user object
 * @param {String} tileLogoId - UID of the tileLogo document
 * @param {File} tileLogoFile - Svg file of the tileLogo
 * @param {String} combinedName - Combined name of the tileLogo. getCombinedName() method can be used to obtain the combined name from the name object.
 * @return {Promise} Promise which succeeds once the request is successful.
 */
export async function updateTileLogo(userId, tileLogoId, tileLogoFile, combinedName) {
    // upload image to firebase storage
    var fileUrl = await uploadTileLogoFile(combinedName, tileLogoFile);
    // Update url of uploaded image to created record
    return await addFileUrlForTileLogo(tileLogoId, userId, fileUrl);
}

/**
 * Checks if the tileLogo with the given name already exists or not
 *
 * @param {String} combinedName - Combined name of the tileLogo. getCombinedName() method can be used to obtain the combined name from the name object.
 * @return {Promise} - Promise which succeeds if the tileLogo name is unique else throws an error
 */
async function isTileLogoNameUnique(combinedName) {
    // check on firebase if tileLogo with same name already exists
    var db = firebase.firestore();
    var querySnapshot = await db
        .collection('tile_logo')
        .where('combined_name', '==', combinedName)
        .limit(1)
        .get();
    if (querySnapshot.size > 0) {
        throw new Error('Tile Logo with the same name already exists');
    } else {
        return;
    }
}

/**
 * Created document for an tileLogo on the database
 *
 * @param {Object} name - Name object of the tileLogo
 * @param {String} combinedName - Combined name of the tileLogo. getCombinedName() method can be used to obtain the combined name from the name object.
 * @param {String} userId - Id of the user obtained from the user object
 * @return {Promise} - Promise which succeeds when the tileLogo is successfully created, else throws an error.
 */
async function createTileLogoRecordOnDb(name, combinedName, userId) {
    var db = firebase.firestore();
    var tileLogoCollectionRef = db.collection('tile_logo');

    var tileLogoObj = {
        combined_name: combinedName,
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
        created_by: userId,
        last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        last_updated_by: userId,
    };

    const docRef = await tileLogoCollectionRef.add(tileLogoObj);
    return docRef.id;
}

/**
 * Updates tileLogo document's image_url field
 *
 * @param {String} recordId - UID of the tileLogo document
 * @param {String} userId - Id of the user obtained from the user object
 * @param {String} fileUrl - Url of the uploaded file
 * @return {Promise} - Promise that succeeds when the updation of the field is successful.
 */
async function addFileUrlForTileLogo(recordId, userId, fileUrl) {
    var db = firebase.firestore();
    var tileLogoCollectionRef = db.collection('tile_logo');

    return await tileLogoCollectionRef.doc(recordId).update({
        img_url: fileUrl,
        last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        last_updated_by: userId,
    });
}

/**
 * Uploads an tileLogo file to storage and returns url of the uploaded file
 *
 * @param {String} combinedTileLogoName - Combined name of the tileLogo. getCombinedName() method can be used to obtain the combined name from the name object.
 * @param {File} file - Svg file of the tileLogo
 * @return {Promise} - Promise that succeeds once the uploading of the tileLogo file is successful. The promise contains the download url of the file.
 */
async function uploadTileLogoFile(combinedTileLogoName, file) {
    // Identifier could be default or region identifier or event identifier
    const storage = firebase.storage();
    var tileLogoRootFolderRef = storage.ref('coffer/tile_logo');
    var tileLogoImageRef = tileLogoRootFolderRef.child(`${combinedTileLogoName}.svg`);
    const maxAge = 90 * 24 * 60 * 60; // days * hours * minutes * seconds
    var metadata = {
        cacheControl: `max-age=${maxAge}`,
    };
    await tileLogoImageRef.put(file, metadata);
    const url = await tileLogoImageRef.getDownloadURL();
    return url;
}
